import React, { useEffect, useState } from 'react';
import { List } from '@refinedev/antd';
import { Button, Modal, Row } from 'antd';
import { httpClient, ROOT_URL } from '../../App';
import { getETHPrice, getUserWallet, getVTBalance } from '../../utils/blockchain';
import { getPayTokenContract } from '../../common/smart-contracts/helpers/PayToken/PayToken';
import { formatUnits, Typed } from 'ethers';
import { handleConnect } from '../../api/wallet';
import { TokensWithdrawalModal } from '../../components/modal/tokensWithdrawalModal';
import { ETHPrice } from '../../common/payment-types.const';
import { amountParser } from '../../utils/amount.utils';

const isMetaMask = window?.ethereum?.isMetaMask ?? false;

type WalletCaveats = {
  value: Array<string>;
};

type WalletPermission = {
  caveats: Array<WalletCaveats>;
};

export const Wallet = () => {
  const [userWallet, setUserWallet] = useState<string | null>(null);
  const [SoVBalance, setSoVBalance] = useState<string>('N/A');
  const [vtBalance, setVtBalance] = useState<string>('N/A');
  const [walletDisconnectConfirmationModalOpened, setWalletDisconnectConfirmationModalOpened] =
    useState<boolean>(false);
  const [withdrawTokensModalOpened, setWithdrawTokensModalOpened] = useState<boolean>(false);
  const [ethPrice, setETHPrice] = useState<ETHPrice>();

  useEffect(() => {
    (async () => {
      try {
        const result = await getUserWallet();
        if (result.status === 200) {
          setUserWallet(result.data.wallet);
        }
      } catch {
        setUserWallet(null);
      }
    })();
  }, []);

  useEffect(() => {
    if (userWallet) {
      (async () => {
        const result = await getVTBalance();

        if (result.status === 200) {
          setVtBalance(result.data);
        }
      })();
    } else {
      setVtBalance('N/A');
    }
  }, [userWallet]);

  useEffect(() => {
    if (userWallet) {
      (async () => {
        const contract = await getPayTokenContract(userWallet);

        if (contract) {
          try {
            const balance = await contract.balanceOf(Typed.address(userWallet), 0);
            setSoVBalance(balance ? formatUnits(balance, 'ether') : '0');
          } catch (e) {
            console.error(e);
          }
        }
      })();
    }
  }, [userWallet]);

  useEffect(() => {
    (async () => {
      const permissions: Array<WalletPermission> = await window.ethereum.request({
        method: 'wallet_getPermissions',
      });

      if (
        userWallet &&
        (!permissions.length ||
          !permissions.some((permission: WalletPermission) =>
            permission.caveats.some((caveat: WalletCaveats) => caveat.value.includes(userWallet)),
          ))
      ) {
        await disconnectWallet();
      }
    })();
  }, [userWallet]);

  useEffect(() => {
    if (!ethPrice) {
      (async () => {
        const ethPrice = await getETHPrice();
        if (ethPrice.status === 200) {
          setETHPrice(ethPrice.data);
        }
      })();
    }
  }, [ethPrice]);

  async function disconnectWallet() {
    setWalletDisconnectConfirmationModalOpened(false);
    setUserWallet(null);
    await httpClient.delete(`${ROOT_URL}/api/v1/blockchain/wallet`);
  }

  function getHeaderButton() {
    if (!isMetaMask) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
          <Button style={{ borderRadius: 0 }} disabled type='primary'>
            {userWallet ? 'Disconnect wallet' : 'Connect wallet'}
          </Button>
          <span style={{ color: 'red' }}>
            <a href='https://metamask.io/download/'>MetaMask</a> browser extension is not installed
          </span>
        </div>
      );
    }

    if (!userWallet) {
      return (
        <Button
          type='primary'
          style={{ borderRadius: 0 }}
          onClick={async () => {
            const wallet = await handleConnect();
            setUserWallet(wallet);
          }}
        >
          Connect wallet
        </Button>
      );
    }

    return <Button onClick={() => setWalletDisconnectConfirmationModalOpened(true)}>Disconnect wallet</Button>;
  }

  return (
    <>
      <List title='Wallet' headerButtons={getHeaderButton()}>
        <div style={{ marginTop: 40 }}>
          <p>
            <b>Ledger Balance: </b>
            {vtBalance !== 'N/A'
              ? amountParser((parseFloat(vtBalance) * (ethPrice?.USD ?? 1)).toString())
              : vtBalance}{' '}
            USD / {vtBalance} SMART Tokens (ETH)
          </p>
          <p>
            <b>Available Balance: </b>{' '}
            {SoVBalance !== 'N/A'
              ? amountParser((parseFloat(SoVBalance) * (ethPrice?.USD ?? 1)).toString())
              : SoVBalance}{' '}
            USD / {SoVBalance} SMART Tokens (ETH)
          </p>
          {userWallet && (
            <p>
              <b>Withdraw tokens to your wallet</b>{' '}
              <Button onClick={() => setWithdrawTokensModalOpened(true)}>Withdraw Tokens</Button>
            </p>
          )}
          <p>
            <b>Wallet: </b> {userWallet ?? 'Not connected'}
          </p>
        </div>
      </List>

      <Modal
        title='Warning'
        footer={
          <Row style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={disconnectWallet}>Disconnect</Button>
            <Button type='primary' onClick={() => setWalletDisconnectConfirmationModalOpened(false)}>
              Cancel
            </Button>
          </Row>
        }
        open={walletDisconnectConfirmationModalOpened}
        onCancel={() => setWalletDisconnectConfirmationModalOpened(false)}
      >
        <p>Are you sure you want to disconnect your wallet? You can reconnect it later.</p>
      </Modal>
      <TokensWithdrawalModal
        modalOpened={withdrawTokensModalOpened}
        closeModal={() => setWithdrawTokensModalOpened(false)}
        paymentDetails={{
          availableBalance: SoVBalance,
          userWallet,
        }}
      />
    </>
  );
};
